import { Component, Input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { faFile, faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import { ItemListComponent, ListConfig, ListItem } from '../item-list/item-list.component';
import { DateFormatterService } from '../../services/date-formatter.service';
import { TenantService } from '../../services/tenant.service';
import { normalizeForSearch } from '../../utils/text-search.util';

export type SimpleSimulationType = 'retriever' | 'processing';

export interface SimulationExecutionListItem {
  id: string;
  created_at: string;
  user?: string;
  config?: Record<string, any> | null;
}

@Component({
  selector: 'app-simulation-executions-list',
  standalone: true,
  imports: [FormsModule, FontAwesomeModule, TranslateModule, ItemListComponent],
  templateUrl: './simulation-executions-list.component.html',
  styleUrls: ['./simulation-executions-list.component.scss'],
})
export class SimulationExecutionsListComponent {
  @Input() simulationId = '';
  @Input() simulationType: SimpleSimulationType = 'processing';
  @Input() executions: SimulationExecutionListItem[] = [];
  @Input() loading = false;

  searchText = '';
  readonly icons = { faSearch, faTimes };
  readonly listConfig: ListConfig = {
    itemIcon: faFile,
    itemIconClass: 'result-icon',
    showMetadata: true,
    showType: true,
    typeLabels: {
      COMPLETED: 'INTERACTIONS.RESULTS.STATUS.COMPLETED',
      PENDING: 'INTERACTIONS.RESULTS.STATUS.RUNNING',
      PARTIALLY_COMPLETED: 'INTERACTIONS.RESULTS.STATUS.PARTIALLY_COMPLETED',
    },
    emptyMessage: 'INTERACTIONS.RESULTS.NO_RESULTS',
    emptyMessageFiltered: 'INTERACTIONS.RESULTS.NO_RESULTS',
    actions: [],
  };

  constructor(
    private router: Router,
    private tenantService: TenantService,
    private translate: TranslateService,
    private dateFormatter: DateFormatterService,
  ) {}

  get listItems(): ListItem[] {
    return this.executions.map((execution, index) => {
      const status = this.executionStatus(execution);
      return {
        id: execution.id,
        name: this.translate.instant('INTERACTIONS.RESULTS.RESULT_NAME', {
          number: this.executions.length - index,
        }),
        created_date: execution.created_at ? this.dateFormatter.formatDate(execution.created_at) : '',
        created_time: execution.created_at ? this.dateFormatter.formatTime(execution.created_at) : '',
        type: status,
        status,
        user: execution.user,
      };
    });
  }

  private executionStatus(execution: SimulationExecutionListItem): string {
    switch (execution.config?.['status']) {
      case 'running':
        return 'PENDING';
      case 'completed_with_errors':
        return 'PARTIALLY_COMPLETED';
      default:
        return 'COMPLETED';
    }
  }

  get filteredItems(): ListItem[] {
    if (!this.searchText.trim()) return this.listItems;
    const search = normalizeForSearch(this.searchText);
    return this.listItems.filter(item =>
      normalizeForSearch(item.name).includes(search) ||
      normalizeForSearch(item['user'] || '').includes(search)
    );
  }

  clearSearch(): void {
    this.searchText = '';
  }

  openExecution(item: ListItem): void {
    const segment = this.simulationType === 'retriever' ? 'retrievers' : 'processings';
    const resultNumber = this.executions.length - this.executions.findIndex(execution => execution.id === item.id);
    this.router.navigate(
      this.tenantService.buildRoute([segment, this.simulationId, 'results', item.id]),
      { queryParams: { resultNumber } }
    );
  }
}
